import { Autocomplete, Grid, Typography } from '@mui/material'
import { Field, FieldProps } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { VinciInput } from '~/components'
import { VinciAccordion } from '~/components/VinciAccordion/VinciAccordion'
import { useFormikUtils } from '~/hooks/useFormikUtils'

const typeOptions = ['Commercial', 'Residential', 'Mixed Use'].map((i) => i.toUpperCase())

interface IRealStateAssetFormProps {
  singleTitle?: string
  shouldShowOptionalFields?: boolean
}

export const RealStateAssetForm: FC<IRealStateAssetFormProps> = ({
  singleTitle,
  shouldShowOptionalFields = true,
}) => {
  const { getInputErrorProps } = useFormikUtils()

  const { t } = useTranslation()

  return (
    <Grid
      container
      justifyContent={'center'}
      alignItems='center'
      sx={{ width: '100%', maxWidth: 600 }}
    >
      <Grid item xs={12} px={1} py={1}>
        <Typography variant='overline'>{t(singleTitle ?? 'Asset Identification')}</Typography>
      </Grid>

      <Grid item xs={12} px={1} py={1}>
        <Field name='property_name'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Property Name')}
              {...props.field}
              {...getInputErrorProps('property_name')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} px={1} py={1}>
        <Field name='address_line'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Address Line')}
              {...props.field}
              {...getInputErrorProps('address_line')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} md={6} px={1} py={1}>
        <Field name='suite_number'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Suite Number')}
              {...props.field}
              {...getInputErrorProps('suite_number')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} md={6} px={1} py={1}>
        <Field name='zip'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('ZIP/Postal Code')}
              {...props.field}
              {...getInputErrorProps('zip')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6} px={1} py={1}>
        <Field name='city'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('City')}
              {...props.field}
              {...getInputErrorProps('city')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6} px={1} py={1}>
        <Field name='state'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('State')}
              {...props.field}
              {...getInputErrorProps('state')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} px={1} py={1}>
        <Field name='extra_fields.type'>
          {(props: FieldProps) => (
            <Autocomplete
              options={typeOptions}
              {...props.field}
              onChange={(_, value: string) => {
                props.form.setFieldValue('extra_fields.type', value ?? '')
              }}
              value={props.field.value ?? ''}
              renderInput={(params) => {
                return (
                  <VinciInput
                    label={t('Type')}
                    {...getInputErrorProps('extra_fields.type')}
                    {...params}
                    inputProps={{ style: { height: 10 }, ...params.inputProps }}
                  />
                )
              }}
            />
          )}
        </Field>
      </Grid>

      {shouldShowOptionalFields && (
        <Grid item xs={12} px={1} py={1} mt={2}>
          <VinciAccordion
            styles={{
              contentSx: {
                padding: '2',
                wrapperSx: {
                  boxShadow: 'none',
                  borderRadius: '0 !important',
                  borderTop: '0 !important',
                },
              },
            }}
            key='Additional'
            header={{
              title: `${t('Additional')}`,
              description: `${t('Optional Fields')}`,
            }}
          >
            <Field name='id_external_system'>
              {(props: FieldProps) => (
                <VinciInput
                  label={t('ID External System')}
                  {...props.field}
                  {...getInputErrorProps('id_external_system')}
                  sx={{ mb: 1 }}
                  fullWidth
                  onChange={props.form.handleChange}
                />
              )}
            </Field>
          </VinciAccordion>
        </Grid>
      )}
    </Grid>
  )
}
